import { Box, Typography } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContainerIcon({ status, showLabel = false, type = 'other' }) {
  const { t } = useTranslation();
  const globalClipId = useId();
  const getFillingPercentage = () => {
    switch (status) {
      case 'empty':
        return 0;
      case 'draining':
        return 50;
      case 'full':
        return 100;
      default:
        return null;
    }
  };

  const getBorderColor = () => {
    switch (status) {
      case 'empty':
        return 'gray';
      case 'draining':
        return '#f4ad45';
      case 'full':
        return '#f4ad45';
      default:
        return '#gray';
    }
  };

  const layingBarrelStyle = {
    width: 25,
    height: 25,
    border: '3px solid gray',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const standingBarrelStyle = {
    width: 21,
    height: 21,
    borderRadius: '10% / 50%',
    outline: '3.5px solid gray',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const otherContainerStyle = {
    width: 25,
    height: 25,
    border: '3px solid gray',
    borderRadius: '0%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const backgroundGradient = `linear-gradient(to top, ${getBorderColor()} ${getFillingPercentage()}%, 
      transparent ${getFillingPercentage()}%)`;

  // Weighed-container special icon (weight): ring + trapezoid body, stroke only (empty inside)
  if (type === 'weighed-container') {
    const clipId = globalClipId; // use a stable id regardless of branch
    const fillPercent = getFillingPercentage() || 0;
    const bodyTop = 14; // y of trapezoid top
    const bodyBottom = 32; // y of trapezoid bottom
    const bodyHeight = bodyBottom - bodyTop; // 18
    const fillHeight = (bodyHeight * fillPercent) / 100;
    const fillY = bodyBottom - fillHeight;
    const fillColor = getBorderColor();
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1,
      }}
      >
        <svg width="32" height="34" viewBox="0 0 32 34" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <clipPath id={clipId}>
              <polygon points="6,14 26,14 30,32 2,32" />
            </clipPath>
          </defs>
          {/* Filling inside trapezoid clipped area */}
          <rect x="0" y={fillY} width="32" height={fillHeight} fill={fillColor} clipPath={`url(#${clipId})`} />
          {/* Ring */}
          <circle cx="16" cy="6" r="4" stroke="gray" strokeWidth="3" fill="none" />
          {/* Trapezoid body outline */}
          <polygon points="6,14 26,14 30,32 2,32" stroke="gray" strokeWidth="3" fill="none" />
        </svg>
        {showLabel && (
          <Typography sx={{ width: 50, textAlign: 'left' }}>
            {t(`inventory_item.filling.${status}`)}
          </Typography>
        )}
      </Box>
    );
  }

  if (type === 'truncated-cone') {
    const clipId = `${globalClipId}-truncated`;
    const fillPercent = getFillingPercentage() || 0;
    const bodyTop = 6;
    const bodyBottom = 32;
    const bodyHeight = bodyBottom - bodyTop;
    const fillHeight = (bodyHeight * fillPercent) / 100;
    const fillY = bodyBottom - fillHeight;
    const fillColor = getBorderColor();

    return (
      <Box sx={{
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1,
      }}
      >
        <svg width="32" height="34" viewBox="0 0 32 34" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <clipPath id={clipId}>
              <polygon points="9.6,6 22.4,6 28,32 4,32" />
            </clipPath>
          </defs>
          <rect x="0" y={fillY} width="32" height={fillHeight} fill={fillColor} clipPath={`url(#${clipId})`} />
          <polygon points="9.6,6 22.4,6 28,32 4,32" stroke="gray" strokeWidth="3" fill="none" />
        </svg>
        {showLabel && (
          <Typography sx={{ width: 50, textAlign: 'left' }}>
            {t(`inventory_item.filling.${status}`)}
          </Typography>
        )}
      </Box>
    );
  }

  let containerStyle;
  if (type === 'standing') {
    containerStyle = standingBarrelStyle;
  } else if (type === 'laying') {
    containerStyle = layingBarrelStyle;
  } else {
    containerStyle = otherContainerStyle;
  }

  containerStyle = {
    ...containerStyle,
    background: backgroundGradient,
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1,
    }}
    >
      <Box sx={containerStyle} />
      {showLabel && (
        <Typography sx={{ width: 50, textAlign: 'left' }}>
          {t(`inventory_item.filling.${status}`)}
        </Typography>
      )}
    </Box>
  );
}
